import type {
  ChapterDeviceLayer,
  ChapterDeviceThread,
  DeviceOccurrenceReference,
  DeviceThreadSuggestionCandidate,
  WitnessDeviceLayerRecord,
  WitnessDeviceOccurrence,
  DeviceOccurrenceSuggestionCandidate,
  DeviceAnnotationStatus,
} from '../types/deviceAnnotationTypes'

export interface DeviceAnnotationMergeReport {
  accepted_ids: string[]
  superseded_ids: string[]
  conflict_candidate_ids: string[]
  suppressed_candidate_ids: string[]
}

const isReviewedStatus = (status: DeviceAnnotationStatus): boolean => (
  status === 'confirmed' || status === 'edited'
)

const normalizeValue = (value?: string): string => (
  (value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
)

const buildOccurrenceIdentityKey = (occurrence: Pick<
  WitnessDeviceOccurrence,
  'start_row_id' | 'end_row_id' | 'type_id' | 'proposed_type_label' | 'label'
>): string => (
  [
    occurrence.start_row_id,
    occurrence.end_row_id,
    normalizeValue(occurrence.type_id),
    normalizeValue(occurrence.proposed_type_label),
    normalizeValue(occurrence.label),
  ].join('::')
)

const buildThreadIdentityKey = (thread: Pick<
  ChapterDeviceThread,
  'canonical_label' | 'type_id' | 'proposed_type_label' | 'occurrence_refs'
>): string => {
  const refs = [...thread.occurrence_refs]
    .map(ref => `${ref.witness_id}:${ref.occurrence_id}`)
    .sort()
    .join('|')
  return [
    refs,
    normalizeValue(thread.type_id),
    normalizeValue(thread.proposed_type_label),
    normalizeValue(thread.canonical_label),
  ].join('::')
}

const appendUniqueOccurrenceCandidate = (
  occurrence: WitnessDeviceOccurrence,
  candidate: DeviceOccurrenceSuggestionCandidate,
): WitnessDeviceOccurrence => {
  const existing = occurrence.candidate_suggestions || []
  if (existing.some(entry => entry.candidate_id === candidate.candidate_id)) {
    return occurrence
  }
  return {
    ...occurrence,
    candidate_suggestions: [...existing, candidate],
  }
}

const appendUniqueThreadCandidate = (
  thread: ChapterDeviceThread,
  candidate: DeviceThreadSuggestionCandidate,
): ChapterDeviceThread => {
  const existing = thread.candidate_suggestions || []
  if (existing.some(entry => entry.candidate_id === candidate.candidate_id)) {
    return thread
  }
  return {
    ...thread,
    candidate_suggestions: [...existing, candidate],
  }
}

const isActiveOccurrence = (occurrence: WitnessDeviceOccurrence): boolean => !occurrence.superseded_by_occurrence_id
const isActiveThread = (thread: ChapterDeviceThread): boolean => !thread.superseded_by_thread_id

export const getActiveWitnessDeviceOccurrences = (
  layer: WitnessDeviceLayerRecord | undefined | null,
): WitnessDeviceOccurrence[] => (
  Array.isArray(layer?.occurrences)
    ? layer!.occurrences.filter(isActiveOccurrence)
    : []
)

export const getActiveChapterDeviceThreads = (
  layer: ChapterDeviceLayer | undefined | null,
): ChapterDeviceThread[] => (
  Array.isArray(layer?.thread_map)
    ? layer!.thread_map.filter(isActiveThread)
    : []
)

export const mergeWitnessDeviceSuggestions = ({
  existingLayer,
  incomingOccurrences,
  runId,
  createdAt,
}: {
  existingLayer: WitnessDeviceLayerRecord
  incomingOccurrences: WitnessDeviceOccurrence[]
  runId: string
  createdAt?: string
}): {
  layer: WitnessDeviceLayerRecord
  report: DeviceAnnotationMergeReport
} => {
  const timestamp = createdAt || new Date().toISOString()
  const nextOccurrences = [...existingLayer.occurrences]
  const report: DeviceAnnotationMergeReport = {
    accepted_ids: [],
    superseded_ids: [],
    conflict_candidate_ids: [],
    suppressed_candidate_ids: [],
  }

  incomingOccurrences.forEach(incomingBase => {
    const incoming: WitnessDeviceOccurrence = {
      ...incomingBase,
      source_run_id: incomingBase.source_run_id || runId,
    }
    const identityKey = buildOccurrenceIdentityKey(incoming)
    const matchedIndex = nextOccurrences.findIndex(candidate =>
      isActiveOccurrence(candidate)
      && buildOccurrenceIdentityKey(candidate) === identityKey
    )

    if (matchedIndex < 0) {
      nextOccurrences.push(incoming)
      report.accepted_ids.push(incoming.occurrence_id)
      return
    }

    const matched = nextOccurrences[matchedIndex]

    if (matched.status === 'rejected') {
      nextOccurrences[matchedIndex] = appendUniqueOccurrenceCandidate(matched, {
        candidate_id: incoming.occurrence_id,
        source_run_id: incoming.source_run_id,
        category_id: incoming.category_id,
        type_id: incoming.type_id,
        proposed_type_label: incoming.proposed_type_label,
        label: incoming.label,
        ...(incoming.summary ? { summary: incoming.summary } : {}),
        start_row_id: incoming.start_row_id,
        end_row_id: incoming.end_row_id,
        evidence_row_ids: incoming.evidence_row_ids,
        ...(typeof incoming.confidence === 'number' ? { confidence: incoming.confidence } : {}),
        created_at: timestamp,
        resolution: 'suppressed-as-rejected',
        reason: 'matches-rejected',
      })
      report.suppressed_candidate_ids.push(incoming.occurrence_id)
      return
    }

    if (isReviewedStatus(matched.status)) {
      nextOccurrences[matchedIndex] = appendUniqueOccurrenceCandidate(matched, {
        candidate_id: incoming.occurrence_id,
        source_run_id: incoming.source_run_id,
        category_id: incoming.category_id,
        type_id: incoming.type_id,
        proposed_type_label: incoming.proposed_type_label,
        label: incoming.label,
        ...(incoming.summary ? { summary: incoming.summary } : {}),
        start_row_id: incoming.start_row_id,
        end_row_id: incoming.end_row_id,
        evidence_row_ids: incoming.evidence_row_ids,
        ...(typeof incoming.confidence === 'number' ? { confidence: incoming.confidence } : {}),
        created_at: timestamp,
        resolution: 'pending-review',
        reason: 'conflicts-with-reviewed',
      })
      report.conflict_candidate_ids.push(incoming.occurrence_id)
      return
    }

    nextOccurrences[matchedIndex] = {
      ...matched,
      superseded_by_occurrence_id: incoming.occurrence_id,
    }
    nextOccurrences.push({
      ...incoming,
      supersedes_occurrence_ids: Array.from(new Set([...(incoming.supersedes_occurrence_ids || []), matched.occurrence_id])),
    })
    report.accepted_ids.push(incoming.occurrence_id)
    report.superseded_ids.push(matched.occurrence_id)
  })

  return {
    layer: {
      ...existingLayer,
      occurrences: nextOccurrences,
      updated_at: timestamp,
    },
    report,
  }
}

export const mergeChapterDeviceThreadSuggestions = ({
  existingLayer,
  incomingThreads,
  runId,
  createdAt,
}: {
  existingLayer: ChapterDeviceLayer
  incomingThreads: ChapterDeviceThread[]
  runId: string
  createdAt?: string
}): {
  layer: ChapterDeviceLayer
  report: DeviceAnnotationMergeReport
} => {
  const timestamp = createdAt || new Date().toISOString()
  const nextThreads = [...existingLayer.thread_map]
  const nextThreadOrder = [...existingLayer.thread_order]
  const report: DeviceAnnotationMergeReport = {
    accepted_ids: [],
    superseded_ids: [],
    conflict_candidate_ids: [],
    suppressed_candidate_ids: [],
  }

  incomingThreads.forEach(incomingBase => {
    const incoming: ChapterDeviceThread = {
      ...incomingBase,
      source_run_id: incomingBase.source_run_id || runId,
    }
    const identityKey = buildThreadIdentityKey(incoming)
    const matchedIndex = nextThreads.findIndex(candidate =>
      isActiveThread(candidate)
      && buildThreadIdentityKey(candidate) === identityKey
    )

    if (matchedIndex < 0) {
      nextThreads.push(incoming)
      if (!nextThreadOrder.includes(incoming.thread_id)) {
        nextThreadOrder.push(incoming.thread_id)
      }
      report.accepted_ids.push(incoming.thread_id)
      return
    }

    const matched = nextThreads[matchedIndex]

    if (matched.status === 'rejected') {
      nextThreads[matchedIndex] = appendUniqueThreadCandidate(matched, {
        candidate_id: incoming.thread_id,
        source_run_id: incoming.source_run_id,
        canonical_label: incoming.canonical_label,
        category_id: incoming.category_id,
        type_id: incoming.type_id,
        proposed_type_label: incoming.proposed_type_label,
        occurrence_refs: incoming.occurrence_refs,
        created_at: timestamp,
        resolution: 'suppressed-as-rejected',
        reason: 'matches-rejected',
      })
      report.suppressed_candidate_ids.push(incoming.thread_id)
      return
    }

    if (isReviewedStatus(matched.status)) {
      nextThreads[matchedIndex] = appendUniqueThreadCandidate(matched, {
        candidate_id: incoming.thread_id,
        source_run_id: incoming.source_run_id,
        canonical_label: incoming.canonical_label,
        category_id: incoming.category_id,
        type_id: incoming.type_id,
        proposed_type_label: incoming.proposed_type_label,
        occurrence_refs: incoming.occurrence_refs,
        created_at: timestamp,
        resolution: 'pending-review',
        reason: 'conflicts-with-reviewed',
      })
      report.conflict_candidate_ids.push(incoming.thread_id)
      return
    }

    nextThreads[matchedIndex] = {
      ...matched,
      superseded_by_thread_id: incoming.thread_id,
    }
    nextThreads.push({
      ...incoming,
      supersedes_thread_ids: Array.from(new Set([...(incoming.supersedes_thread_ids || []), matched.thread_id])),
    })
    if (!nextThreadOrder.includes(incoming.thread_id)) {
      nextThreadOrder.push(incoming.thread_id)
    }
    report.accepted_ids.push(incoming.thread_id)
    report.superseded_ids.push(matched.thread_id)
  })

  return {
    layer: {
      ...existingLayer,
      thread_map: nextThreads,
      thread_order: nextThreadOrder,
      updated_at: timestamp,
    },
    report,
  }
}

export const reviewWitnessDeviceOccurrence = ({
  layer,
  occurrenceId,
  nextStatus,
  reviewer,
  updates,
  reviewedAt,
}: {
  layer: WitnessDeviceLayerRecord
  occurrenceId: string
  nextStatus: Extract<DeviceAnnotationStatus, 'confirmed' | 'edited' | 'rejected'>
  reviewer: string
  updates?: Partial<Pick<
    WitnessDeviceOccurrence,
    'category_id' | 'type_id' | 'proposed_type_label' | 'label' | 'summary' | 'notes' | 'evidence_row_ids'
  >>
  reviewedAt?: string
}): WitnessDeviceLayerRecord => {
  const timestamp = reviewedAt || new Date().toISOString()
  return {
    ...layer,
    occurrences: layer.occurrences.map(occurrence =>
      occurrence.occurrence_id !== occurrenceId
        ? occurrence
        : {
            ...occurrence,
            ...updates,
            status: nextStatus,
            review: {
              reviewed_at: timestamp,
              reviewed_by: reviewer,
            },
          }
    ),
    updated_at: timestamp,
  }
}

export const reviewChapterDeviceThread = ({
  layer,
  threadId,
  nextStatus,
  reviewer,
  updates,
  reviewedAt,
}: {
  layer: ChapterDeviceLayer
  threadId: string
  nextStatus: Extract<DeviceAnnotationStatus, 'confirmed' | 'edited' | 'rejected'>
  reviewer: string
  updates?: Partial<Pick<
    ChapterDeviceThread,
    'canonical_label' | 'category_id' | 'type_id' | 'proposed_type_label' | 'notes' | 'occurrence_refs'
  >>
  reviewedAt?: string
}): ChapterDeviceLayer => {
  const timestamp = reviewedAt || new Date().toISOString()
  return {
    ...layer,
    thread_map: layer.thread_map.map(thread =>
      thread.thread_id !== threadId
        ? thread
        : {
            ...thread,
            ...updates,
            status: nextStatus,
            review: {
              reviewed_at: timestamp,
              reviewed_by: reviewer,
            },
          }
    ),
    updated_at: timestamp,
  }
}

export const buildOccurrenceReferenceKey = (reference: DeviceOccurrenceReference): string => (
  `${reference.witness_id}:${reference.occurrence_id}`
)
